import * as d3 from '~/app/custom-d3'
import numeral from 'numeral'

export default class GrowthGraph {
  constructor(el, funds, investedAmount) {
    this.el = el
    this.activeFund = null
    this.funds = funds
    this.fundEntries = d3.entries(funds)
    this.investedAmount = investedAmount
    this.margin = {
      top: 20,
      left: 70,
      right: 40,
      bottom: 30
    }

    this.options = this.generateBounds()
    this.setup()

    this.callbacks = {};
  }

  on(event, callback) {
    this.callbacks[event] = callback
  }

  generateBounds() {
    var bounds = this.el.getBoundingClientRect(),
        height = 300,
        width = 1100
    if(height > bounds.width) {
      height = bounds.width * 0.8
    }
    if(width > bounds.width) {
      width = bounds.width
    }
    return {
      height: height,
      width: width
    }
  }

  setup() {
    this.svg = d3.select(this.el).append("svg")
        .attr('class', 'graph')
        .attr('width', this.options.width)
        .attr('height', this.options.height);

    if ('ontouchstart' in document) {
      this.svg.style('-webkit-tap-highlight-color', 'transparent')
         .on('touchmove', this.moved.bind(this))
         .on('touchend', this.left.bind(this))
    } else {
      this.svg.on('mousemove', this.moved.bind(this))
              .on('mouseleave', this.left.bind(this));
    }

    // X-axis
    this.xAxisEl = this.svg.append("g")
                 .attr("class", "x axis")
                 .attr("transform", "translate("+this.margin.left+"," + (this.options.height-this.margin.top-10) + ")");
    this.xAxisEl.append("text")
           .attr("class", "text-end")
           .attr("y", -18)
           .attr("x", this.options.width-this.margin.right-this.margin.left-10)
           .attr("dy", ".71em")
           .text("Year");

    // Y-axis
    this.yAxisEl = this.svg.append("g")
                 .attr("class", "y axis")
                 .attr("transform", "translate("+this.margin.left+","+this.margin.top+")");
    this.yAxisEl.append("text")
           .attr("class", "text-end rotate--90")
           .attr("y", 6)
           .attr("x", this.margin.top*-1)
           .attr("dy", ".71em")
           .text("Value");

    // Lines
    this.lines = this.svg.append("g")
                         .attr("transform", "translate("+this.margin.left+","+this.margin.top+")")

    // Dot for active point
    this.dot = this.svg.append('g')
                       .attr('display', 'none');
    this.circle = this.dot.append('circle')
                          .attr('r', 6);
    this.tip = d3.tip().attr('class', 'd3-tip rounded').html(function(message) {
       return `<p class='font-bold'>${message}</p>`;
    }).offset([-12, 0]);
    this.svg.call(this.tip)

  }

  update(funds, data, yearRange) {
    this.years = d3.range(yearRange[0], yearRange[1]+1)
    this.funds = funds
    this.fundEntries = d3.entries(funds)
    this.growthData = this.generateGrowthData(data, yearRange[0], yearRange[1])

    this.updateYAxis(this.growthData)
    this.updateXAxis(yearRange)
    this.updateGrowthLines(this.growthData, yearRange[0])
  }

  generateGrowthData(data, startYear, endYear) {
    return this.fundEntries.map((fund, index) => {
      return {
        fund: fund,
        returns: this.fundReturns(fund.key, data, startYear, endYear)
      }
    })
  }

  // Generates an array of returns for this fund
  fundReturns(symbol, data, startYear, endYear) {
    // Limit the data to the year range
    var yearlyData = data.filter((d) => ((d.year >= startYear) && (d.year < endYear)))

    var amount = this.investedAmount
    var returns = yearlyData.map((yearData) => {
      amount = amount * (1 + yearData.results[symbol]/100);
      return amount;
    })
    returns.unshift(this.investedAmount)
    return returns
  }

  // X-axis is for the risk
  updateXAxis(years) {
    this.xScale = d3.scaleLinear()
        .domain(years)
        .range([0, this.options.width - this.margin.left - this.margin.right]);
    var xAxis = d3.axisBottom().scale(this.xScale).tickFormat((d) => d).ticks(3)
    this.xAxisEl.call(xAxis);
  }

  // Y-axis is the average returns
  updateYAxis(growthData) {
    var maxValue = d3.max(growthData, (d) => d3.max(d.returns)),
        minValue = d3.min(growthData, (d) => d3.min(d.returns));
    if(minValue > 0) { minValue = 0; }
    this.yScale = d3.scaleLinear()
        .domain([maxValue+1000, minValue])
        .range([0, this.options.height - this.margin.bottom - this.margin.top])

    var yAxis = d3.axisLeft().scale(this.yScale).ticks(6).tickFormat((d) => `${numeral(d).format('$0,0')}`)
    this.yAxisEl.call(yAxis);
  }

  updateGrowthLines(growthData, startYear) {
    var line = d3.line()
                 .x((d, i) => this.xScale(startYear + i))
                 .y((d, i) => this.yScale(d))

    this.lines.selectAll('path')
              .data(growthData)
              .enter()
              .append('path')
              .attr('class', 'line stroke-width-sm blend-multiply rendering-crisp linejoin-round linecap-round')
    this.lines.selectAll('path')
              .transition().duration(1000)
    this.lines.selectAll('path')
              .attr('sector', (d) => d.fund.key)
              .attr('d', (d) => line(d.returns))
              .style('stroke', (d) => d.fund.value.style.darkColor)
  }

  moved() {
    d3.event.preventDefault();

    // X/Year - Find the X coordinate for the current hover position
    var exactYear = this.xScale.invert(d3.event.layerX-this.margin.left);
    const upperYearIndex = d3.bisectLeft(this.years, exactYear, 1);
    const lowerYearIndex = upperYearIndex - 1;
    const yearIndex = (exactYear - this.years[lowerYearIndex]) > (this.years[upperYearIndex] - exactYear) ? upperYearIndex : lowerYearIndex;

    // Y/Fund - Find the Y coordinate for the current hover position
    var ym = this.yScale.invert(d3.event.layerY-this.margin.top);
    var newFund = this.growthData.reduce((a, b) => Math.abs(a.returns[yearIndex] - ym) < Math.abs(b.returns[yearIndex] - ym) ? a : b);

    // Highlight the active fund
    if(this.activeFund != newFund) {
      this.activeFund = newFund
      this.callbacks['activate']('')
      setTimeout(() => this.callbacks['activate'](newFund.fund.key), 5)
    }

    var xDot = this.xScale(this.years[yearIndex]) + this.margin.left,
        yDot = this.yScale(this.activeFund.returns[yearIndex]) + this.margin.top;
    this.dot.attr('display', null)
            .attr("transform", `translate(${xDot},${yDot})`)
    this.circle.style('fill', this.activeFund.fund.value.style.darkColor)
    var message = `<span class="text-${this.activeFund.fund.value.style.lightClass}">${this.activeFund.fund.value.label} - ${numeral(this.activeFund.returns[yearIndex]).format('$0,0')}</span>`
    this.tip.style('color', this.activeFund.fund.value.style.darkColor)
            .style('background-color', this.activeFund.fund.value.style.darkColor)
    this.tip.show(message, this.circle.node())
  }

  left() {
    this.dot.attr('display', 'none')
    this.tip.hide()
    this.lines.selectAll('path')
              .style('stroke', (d) => d.fund.value.style.darkColor)
              .classed('stroke-width-sm', true)
              .classed('stroke-width-lg', false)
  }

  highlight(sector) {
    this.lines.selectAll('path')
              .style('stroke', '')
              .classed('z-10', false)
              .classed('stroke-grey-300', true)
              .classed('stroke-width-sm', true)
              .classed('stroke-width-lg', false)

    if(sector && (sector.length !== 0)) {
      var line = this.lines.select(`[sector=${sector}]`)
                .style('stroke', this.funds[sector].style.darkColor)
                .classed('z-10', true)
                .classed('stroke-grey-300', false)
                .classed('stroke-width-sm', false)
                .classed('stroke-width-lg', true)
    }
  }
}
